import EventHandler from '../_dependencyvendor/event-handler';
import { toHTML } from '../util/dom-util';

const CANVAS_HEIGHT = 218;
const START_Y = 174;
const BAR_WIDTH = 6;
const BAR_SPACING = 10;

class BarChart {
  constructor(element, options) {
    this._element = element;
    this._categories = options.categories;
    this._dataset = options.dataset;
    this._minData = 0;
    this._maxData = 0;
    this._canvas = null;
    this._ctx = null;
    this._tpTrigger = null;
    this._tooltip = null;
    this._objectInfos = [];
    this._devicePixelRatio = 0;
    this._backingStoreRatio = 0;
    this._ratio = 0;
    this._viewWidth = 0;
    this._init();
  }

  update(options) {
    this._categories = options.categories || this._categories;
    this._dataset = options.dataset || this._dataset;
    const minMax = this._getMinMax();
    this._minData = minMax.min;
    this._maxData = minMax.max;
    this._createLegend();
    this._resize();
  }

  _init() {
    this._canvas = document.createElement('canvas');
    this._canvas.height = CANVAS_HEIGHT;
    this._ctx = this._canvas.getContext('2d');
    this._element.appendChild(this._canvas);
    const minMax = this._getMinMax();
    this._minData = minMax.min;
    this._maxData = minMax.max;
    this._tpTrigger = document.querySelector('#tp_trigger');
    this._tooltip = document.querySelector('#bar_chart_tp');
    this._barGroupWidth = 0;
    this._devicePixelRatio = window.devicePixelRatio || 1;
    this._backingStoreRatio =
      this._ctx.webkitBackingStorePixelRatio ||
      this._ctx.mozBackingStorePixelRatio ||
      this._ctx.msBackingStorePixelRatio ||
      this._ctx.oBackingStorePixelRatio ||
      this._ctx.backingStorePixelRatio ||
      1;
    this._ratio = this._devicePixelRatio / this._backingStoreRatio;

    this._initEvents();
    this._createLegend();
    this._resize();
  }

  _initEvents() {
    EventHandler.on(this._canvas, 'click', e => {
      const currentX = e.pageX - this._canvas.getBoundingClientRect().left;
      for (let i = 0; i < this._objectInfos.length; i++) {
        const sx = this._objectInfos[i].startX;
        const ex = this._objectInfos[i].endX;
        if (currentX > sx && ex > currentX) {
          this._updateTooltip(i);
          break;
        }
      }
    });
    EventHandler.on(window, 'resize.chart-bar', this._resize.bind(this));
  }

  _resize() {
    const stageWidth = this._element.getBoundingClientRect().width;
    this._viewWidth = stageWidth;
    this._canvas.style.width = `${stageWidth}px`;
    this._canvas.style.height = `${CANVAS_HEIGHT}px`;
    this._canvas.width = stageWidth * this._ratio;
    this._canvas.height = CANVAS_HEIGHT * this._ratio;
    this._ctx.scale(this._ratio, this._ratio);
    this._drawCanvas();
  }

  _updateTooltip(index) {
    const titleEl = this._tooltip.querySelector('.c-tooltip-box__title');
    const contentEl = this._tooltip.querySelector('.c-tooltip-box__content');
    const curTitle = this._categories[index];
    let contentString = '';
    titleEl.textContent = curTitle;

    for (let i = 0; i < this._dataset.length; i++) {
      const currentTooltip = this._dataset[i].tooltip;
      const currentValue = this._dataset[i].data[index];
      contentString += `<div>${currentTooltip.replace(
        '{value}',
        Number(currentValue).toLocaleString('ko-KR'),
      )}</div>`;
    }
    contentEl.innerHTML = contentString;

    this._tpTrigger.click();
  }

  _getMinMax() {
    let min = 99999;
    let max = -99999;
    for (let i = 0; i < this._categories.length; i++) {
      for (let ii = 0; ii < this._dataset.length; ii++) {
        const currentValue = this._dataset[ii].data[i];
        min = Math.min(min, currentValue);
        max = Math.max(max, currentValue);
      }
    }

    return {
      min,
      max,
    };
  }

  _getBarHeigh(value) {
    const max = this._maxData + 100;
    const per = value / max;
    const perByPx = START_Y * per;
    return perByPx;
    //
  }

  _categoryRender(index, category) {
    const drawInfo = {};
    const width = this._viewWidth / this._categories.length;
    this._ctx.beginPath();
    this._ctx.fillStyle = '#eaeaea';
    this._ctx.fillRect(20, START_Y, this._viewWidth - 40, 1);

    this._ctx.beginPath();
    this._ctx.fillStyle = '#4c4c4c';
    this._ctx.font = '12px KT-sans';
    this._ctx.textAlign = 'center';
    this._ctx.textBaseline = 'top';
    this._ctx.fillText(category, index * width + width / 2, 184, width);

    for (let i = 0; i < this._dataset.length; i++) {
      const currentValue = this._getBarHeigh(this._dataset[i].data[index]);
      const startX = width * index + i * BAR_SPACING;
      const resultX = startX + width / 2 - this._barGroupWidth / 2;
      this._barRender(this._dataset[i].color, currentValue, resultX);
      if (i === 0) {
        drawInfo.startX = resultX;
      } else {
        drawInfo.endX = resultX + BAR_WIDTH;
      }
    }
    return drawInfo;
  }

  _barRender(color, value, x) {
    const radius = 3;
    this._ctx.beginPath();
    this._ctx.fillStyle = color;

    this._ctx.moveTo(x + radius, START_Y - value);
    this._ctx.arcTo(x + BAR_WIDTH, START_Y - value, x + BAR_WIDTH, START_Y - value + value, radius);
    this._ctx.arcTo(x + BAR_WIDTH, START_Y - value + value, x, START_Y - value + value, 0);
    this._ctx.arcTo(x, START_Y - value + value, x, START_Y - value, 0);
    this._ctx.arcTo(x, START_Y - value, x + BAR_WIDTH, START_Y - value, radius);
    this._ctx.fill();
  }

  _getLegend(name, color) {
    const t = `
    <div class="legend-wrap__item">
      <span class="legend-wrap__bullet" style="background-color: ${color}"></span>
      <span class="c-text c-text--type5">${name}</span>
    </div>`;
    return toHTML(t);
  }

  _createLegend() {
    const hasLegendWrap = this._element.querySelector('.legend-wrap');
    if (hasLegendWrap) {
      this._element.removeChild(hasLegendWrap);
    }
    const legendWrap = document.createElement('div');
    const groupChildLength = this._dataset.length;
    legendWrap.className = `legend-wrap`;
    this._element.appendChild(legendWrap);
    for (let i = 0; i < groupChildLength; i++) {
      const dSet = this._dataset[i];
      const color = dSet.color;
      const name = dSet.name;
      legendWrap.appendChild(this._getLegend(name, color));
    }
    this._barGroupWidth = groupChildLength * 6 + BAR_SPACING / 2;
  }

  _drawCanvas() {
    this._ctx.clearRect(0, 0, this._viewWidth, CANVAS_HEIGHT);
    this._objectInfos = [];
    for (let i = 0; i < this._categories.length; i++) {
      const dInfo = this._categoryRender(i, this._categories[i]);
      this._objectInfos.push(dInfo);
    }
    this._ctx.closePath();
  }
}

export default BarChart;
